import { useMany, useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List, EditButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Input, Form, Row, Col } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type {
  OrganizationOut,
  PublisherListOut,
} from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { OrganizationSelectFilter } from '@/components/tenancy/OrganizationSelectFilter';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash } from '@/components/common/show/shared';
import { isFieldFiltered } from '@/utils/filters';
import { formatDate, formatDateTime } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';
import { isContractEditable } from './constants';
import type { ContractOut } from './constants';

export const ContractList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, setFilters, filters, sorters, result } =
    useAdminTable<ContractOut>({
      pagination: { pageSize: 20 },
      resource: 'tenancy.contracts',
      syncWithLocation: true,
      onSearch,
      sorters: {
        initial: [{ field: 'updated_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  const contracts = result?.data ?? [];

  const organizationIds = [...new Set(contracts.map((c) => c.organization_id))];
  const { result: organizationsResult } = useMany<OrganizationOut>({
    resource: 'tenancy.organizations',
    ids: organizationIds,
    queryOptions: { enabled: organizationIds.length > 0 },
  });
  const organizationMap = new Map(
    (organizationsResult?.data ?? []).map((o: OrganizationOut) => [o.id, o])
  );

  const publisherIds = [
    ...new Set(
      contracts
        .map((c) => c.publisher_id)
        .filter((id): id is number => id != null)
    ),
  ];
  const { result: publishersResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: publisherIds,
    queryOptions: { enabled: publisherIds.length > 0 },
  });
  const publisherMap = new Map(
    (publishersResult?.data ?? []).map((p: PublisherListOut) => [p.id, p])
  );

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('tenancy.contracts.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table {...rowNav('tenancy.contracts', tableProps)} rowKey="id">
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="organization_id"
          title={t('tenancy.contracts.fields.organization')}
          sorter
          sortOrder={getSortOrder(sorters, 'organization_id')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'organization_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <OrganizationSelectFilter
              field="organization_id"
              placeholder={t('tenancy.contracts.fields.organization')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(organizationId: number) => (
            <ShowIconLink resource="tenancy.organizations" id={organizationId}>
              {organizationMap.get(organizationId)?.name ?? organizationId}
            </ShowIconLink>
          )}
        />

        <Table.Column
          dataIndex="publisher_id"
          title={t('tenancy.contracts.fields.publisher')}
          render={(publisherId: number | null) =>
            orDash(
              publisherId != null ? (
                <ShowIconLink
                  resource="user_catalog.publishers"
                  id={publisherId}
                >
                  {publisherMap.get(publisherId)?.ancestry_with_name ??
                    publisherId}
                </ShowIconLink>
              ) : (
                t('tenancy.contracts.publisher_default')
              )
            )
          }
        />

        <Table.Column
          dataIndex="valid_from"
          title={t('tenancy.contracts.fields.valid_from')}
          sorter
          sortOrder={getSortOrder(sorters, 'valid_from')}
          render={(value: string) => formatDate(value)}
        />

        <Table.Column
          dataIndex="updated_at"
          title={t('base.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'updated_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="updated_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) => formatDateTime(value)}
        />

        <Table.Column
          title={t('actions.column')}
          dataIndex="actions"
          render={(_, record: ContractOut) =>
            // Contracts already in effect are immutable.
            isContractEditable(record) && (
              <Space>
                <EditButton hideText size="small" recordItemId={record.id} />
                <DeleteButton hideText size="small" recordItemId={record.id} />
              </Space>
            )
          }
        />
      </Table>
    </List>
  );
};
